import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import type { JSX } from "react";
import { DateSeparator } from "../ui/DateSeparator";
import { LetterSeparator } from "../ui/LetterSeparator";
import { groupByDate, groupByLetter } from "../ui/groupHelpers";

export interface ModuleListContentProps<T> {
  readonly items: readonly T[];
  readonly isLoading: boolean;
  readonly hasMore: boolean;
  readonly searchQuery: string;
  readonly onLoadMore: () => void;
  readonly selectedId?: string;
  readonly onSelect: (id: string) => void;
  readonly renderItem: (item: T, selected: boolean) => ReactNode;
  readonly getId: (item: T) => string;
  readonly emptyMessage?: string;
  readonly groupBy?: "date" | "letter";
  readonly getGroupDate?: (item: T) => Date | null;
  readonly getGroupLetter?: (item: T) => string;
  readonly isPinned?: (item: T) => boolean;
}

export function ModuleListContent<T>({
  items,
  isLoading,
  hasMore,
  searchQuery,
  onLoadMore,
  selectedId,
  onSelect,
  renderItem,
  getId,
  emptyMessage,
  groupBy,
  getGroupDate,
  getGroupLetter,
  isPinned,
}: ModuleListContentProps<T>): JSX.Element {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadMoreGuardRef = useRef(false);

  const stableOnLoadMore = useCallback(() => {
    if (loadMoreGuardRef.current) return;
    loadMoreGuardRef.current = true;
    onLoadMore();
    setTimeout(() => {
      loadMoreGuardRef.current = false;
    }, 300);
  }, [onLoadMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !isLoading) {
          stableOnLoadMore();
        }
      },
      { threshold: 0 },
    );

    observer.observe(sentinel);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { observer.disconnect(); };
  }, [hasMore, isLoading, stableOnLoadMore]);

  const groups = useMemo(() => {
    if (groupBy === "date" && getGroupDate) {
      return groupByDate(items, getGroupDate, isPinned);
    }
    if (groupBy === "letter" && getGroupLetter) {
      return groupByLetter(items, getGroupLetter, isPinned);
    }
    return null;
  }, [groupBy, getGroupDate, getGroupLetter, isPinned, items]);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const renderItemNode = (item: T) => {
    const id = getId(item);
    const isSelected = id === selectedId;
    return (
      <div
        key={id}
        className="cursor-pointer"
        onClick={() => { onSelect(id); }}
      >
        {renderItem(item, isSelected)}
      </div>
    );
  };

  return (
    <>
      {groups ? (
        groups.map((group) => (
          <div key={group.key}>
            {group.pinned ? null : (
              groupBy === "date" && group.date ? (
                <DateSeparator date={group.date} />
              ) : (
                <LetterSeparator letter={group.label} />
              )
            )}
            {group.items.map(renderItemNode)}
          </div>
        ))
      ) : (
        items.map(renderItemNode)
      )}
      {isLoading && items.length > 0 && (
        <div className="flex items-center justify-center py-4">
          <span className="text-content-muted text-xs">Loading...</span>
        </div>
      )}
      {items.length === 0 && !isLoading && (
        <div className="py-10 px-4 text-center text-content-tertiary">
          {searchQuery ? "No results found" : (emptyMessage ?? "No items")}
        </div>
      )}
      <div ref={sentinelRef} className="h-1" />
    </>
  );
}
